// <copyright file="quote-action-dialog.ts" company="SoftTech Ltd">
// Copyright (c) SoftTech Ltd. All rights reserved.
// </copyright>

import { html, TemplateResult } from 'lit';
import { LangFranchiseeQuoteListView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-list-view-lang';
import { LitBaseModal } from '../../../../webmodule-common/other/ui/modal/modal-factory-lit';
import { QuoteActionBlockReason, QuoteActionCard, QuoteRowAction } from '../capabilities/quote-row-actions';

/**
 * The row-action surface for a quote, replacing the ellipsis dropdown (Task 229184).
 *
 * Clicking the row's 3-bar icon opens THIS directly — there is deliberately no popup menu that then
 * opens a dialog. The dialog earns its place over a menu in three ways a `webmodule-menu` cannot:
 *  - the header names the quote being acted on, so a mis-clicked row is obvious before anything runs;
 *  - each action carries a legend saying what it does, rather than a bare verb;
 *  - a blocked action explains WHY it is blocked instead of being silently greyed — the actual defect
 *    this replaces, where a user blocked by their own second browser tab had no way to work it out.
 *
 * It returns the chosen {@link QuoteRowAction}, or null if dismissed. The values are the same strings
 * the old menu items used, so the caller's existing dispatch is untouched.
 */
export class QuoteActionDialog extends LitBaseModal {
  private cards: QuoteActionCard[];
  private readonly heading: string;
  private lockOwnerName: string;
  private isClosed = false;

  public selected: QuoteRowAction | null = null;

  constructor(cards: QuoteActionCard[], heading: string, lockOwnerName: string) {
    super();
    this.cards = cards;
    this.heading = heading;
    this.lockOwnerName = lockOwnerName;
  }

  /**
   * @param cards      what to offer, from buildQuoteRowActions(). Built from CACHED row data, so it
   *                   opens with no latency — see `refresh` for how it is corrected.
   * @param heading    already-localised header naming the quote.
   * @param lockOwnerName display name of whoever holds the quote, for the "in use by" reason. Empty
   *                      when nobody else holds it or the name could not be resolved.
   * @param refresh    optional re-read of this ONE quote, run in the BACKGROUND once the dialog is up.
   *                   The row's cached lock state can be stale — an edit lock is released with no event
   *                   and expires on a TTL with nothing running to announce it — and a stale `Locked`
   *                   greys Copy for a quote that is actually free. Awaiting the re-read before opening
   *                   would put a network round-trip in front of every menu, so instead the dialog opens
   *                   immediately and upgrades itself if the answer arrives before the user clicks. If
   *                   they click first, nothing is lost: the server is the real gate and refuses with a
   *                   message. Correctness never depends on this landing in time.
   */
  public static async Execute(
    cards: QuoteActionCard[],
    heading: string,
    lockOwnerName = '',
    refresh?: () => Promise<{ cards: QuoteActionCard[]; lockOwnerName?: string } | null>
  ): Promise<QuoteRowAction | null> {
    const dialog = new QuoteActionDialog(cards, heading, lockOwnerName);
    const shown = dialog.showModal();
    if (refresh) void dialog.applyRefresh(refresh);
    await shown;
    dialog.isClosed = true;
    return dialog.selected;
  }

  /**
   * Swap in freshly-read cards while the dialog is open. Deliberately swallows failures: this is an
   * enhancement over data that is already good enough to act on, so a failed re-read must leave the
   * dialog exactly as it was rather than surfacing an error the user cannot act on.
   */
  private async applyRefresh(
    refresh: () => Promise<{ cards: QuoteActionCard[]; lockOwnerName?: string } | null>
  ): Promise<void> {
    try {
      const fresh = await refresh();
      // The user may have chosen an action, or dismissed, while the re-read was in flight. Re-rendering
      // then would swap the cards out from under a decision already made.
      if (!fresh || this.isClosed || this.selected !== null) return;
      this.cards = fresh.cards;
      if (fresh.lockOwnerName !== undefined) this.lockOwnerName = fresh.lockOwnerName;
      this.requestUpdate();
    } catch {
      /* keep the cached cards */
    }
  }

  protected get modalSize() {
    return 'modal-md';
  }

  get isFooterVisible(): boolean {
    return true;
  }

  title(): string | TemplateResult {
    return this.heading;
  }

  bodyTemplate(): TemplateResult {
    return html`
      <style>
        .quote-action-list {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .quote-action {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.15rem;
          width: 100%;
          padding: 0.75rem 1rem;
          text-align: left;
          border: 1px solid var(--webmodule-color-neutral-300, #d4d4d8);
          border-radius: 0.375rem;
          background: transparent;
          cursor: pointer;
          font: inherit;
          color: inherit;
        }
        .quote-action:hover:not([disabled]) {
          border-color: var(--webmodule-color-primary-500, #6366f1);
          background: var(--webmodule-color-primary-50, #eef2ff);
        }
        .quote-action[disabled] {
          cursor: not-allowed;
          opacity: 0.55;
        }
        /* A temporary block will clear on its own, a structural one will not. Same greyed card, very
           different user response — one means wait, the other means this will never work here. */
        .quote-action[data-block='temporary'] {
          border-style: dashed;
        }
        .quote-action-title {
          font-weight: 600;
        }
        .quote-action-legend {
          font-size: 0.85em;
          opacity: 0.85;
        }
        .quote-action-badge {
          font-size: 0.7em;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          opacity: 0.7;
        }
        /* Separates "make another quote" from "change this quote" from "delete it". Margin does the
           work; the rule is faint because it is a grouping hint, not a section break. */
        .quote-action-divider {
          height: 1px;
          margin: 0.35rem 0;
          background: var(--webmodule-color-neutral-300, #d4d4d8);
        }
      </style>
      <div class="quote-action-list">${this.groupedCards()}</div>
    `;
  }

  /**
   * Render the cards with a rule between groups. "Make me another quote" and "change this quote's
   * state" are different kinds of decision, and an undifferentiated stack of cards made Copy-as-
   * alternative sit flush against Cancel as though they were peers. Grouping lives HERE and not in
   * `buildQuoteRowActions` because it is purely presentational — the capability model decides what a
   * row can do, not how the list is carved up.
   *
   * Relies on `buildQuoteRowActions` emitting in group order (copies, then state changes, then
   * delete), so a rule is needed exactly where consecutive cards disagree.
   */
  private groupedCards(): TemplateResult[] {
    const out: TemplateResult[] = [];
    let previous: QuoteActionGroup | null = null;
    for (const card of this.cards) {
      const group = groupOf(card.action);
      if (previous !== null && group !== previous) {
        out.push(html`<div class="quote-action-divider" role="separator"></div>`);
      }
      out.push(this.cardTemplate(card));
      previous = group;
    }
    return out;
  }

  footerTemplate(): TemplateResult {
    return html`
      <webmodule-button size="small" variant="default" @click=${() => this.hideModal()}>
        ${LangFranchiseeQuoteListView.actionDialogClose()}
      </webmodule-button>
    `;
  }

  private cardTemplate(card: QuoteActionCard): TemplateResult {
    return html`
      <button
        class="quote-action action-${card.action}"
        data-block=${card.blockKind ?? ''}
        ?disabled=${!card.enabled}
        @click=${() => this.choose(card)}
      >
        <span class="quote-action-title">${titleOf(card.presentAs ?? card.action)}</span>
        <span class="quote-action-legend">
          ${card.enabled ? legendOf(card.presentAs ?? card.action) : reasonText(card.reason, this.lockOwnerName)}
        </span>
        ${card.enabled
          ? ''
          : html`<span class="quote-action-badge">${LangFranchiseeQuoteListView.actionDialogUnavailable()}</span>`}
      </button>
    `;
  }

  private async choose(card: QuoteActionCard): Promise<void> {
    if (!card.enabled) return;
    this.selected = card.action;
    await this.hideModal();
  }
}

/**
 * The three kinds of decision this dialog offers, used only to place the dividers.
 *  - `create`      : produces a NEW quote and leaves this one alone.
 *  - `state`       : moves THIS quote through its lifecycle.
 *  - `destructive` : removes it. Separated from `state` so Delete never sits flush against Cancel,
 *                    which are one careless click apart and not remotely equivalent.
 */
type QuoteActionGroup = 'create' | 'state' | 'destructive';

function groupOf(action: QuoteRowAction): QuoteActionGroup {
  switch (action) {
    case 'copyAlternative':
    case 'copyNewProjectQuote':
    case 'copyOutOfProject':
      return 'create';
    case 'delete':
      return 'destructive';
    default:
      return 'state';
  }
}

/** Card titles. Copy reuses the three explicit mode titles rather than one ambiguous "Copy". */
function titleOf(action: QuoteRowAction): string {
  switch (action) {
    case 'copyAlternative':
      return LangFranchiseeQuoteListView.copyAlternativeTitle();
    case 'copyNewProjectQuote':
      return LangFranchiseeQuoteListView.copyNewProjectQuoteTitle();
    case 'copyOutOfProject':
      return LangFranchiseeQuoteListView.copyOutOfProjectTitle();
    case 'cancel':
      return LangFranchiseeQuoteListView.cancelButton();
    case 'accept':
      return LangFranchiseeQuoteListView.acceptButton();
    case 'approve':
      return LangFranchiseeQuoteListView.approveButton();
    case 'reject':
      return LangFranchiseeQuoteListView.rejectButton();
    case 'delete':
      return LangFranchiseeQuoteListView.deleteButton();
    case 'resetToActive':
      return LangFranchiseeQuoteListView.resetToActiveButton();
  }
}

/** What the action does, and why you would pick it. Shown only when the action is available. */
function legendOf(action: QuoteRowAction): string {
  switch (action) {
    case 'copyAlternative':
      return LangFranchiseeQuoteListView.copyAlternativeLegend();
    case 'copyNewProjectQuote':
      return LangFranchiseeQuoteListView.copyNewProjectQuoteLegend();
    case 'copyOutOfProject':
      return LangFranchiseeQuoteListView.copyOutOfProjectLegend();
    case 'cancel':
      return LangFranchiseeQuoteListView.cancelLegend();
    case 'accept':
      return LangFranchiseeQuoteListView.acceptLegend();
    case 'approve':
      return LangFranchiseeQuoteListView.approveLegend();
    case 'reject':
      return LangFranchiseeQuoteListView.rejectLegend();
    case 'delete':
      return LangFranchiseeQuoteListView.deleteLegend();
    case 'resetToActive':
      return LangFranchiseeQuoteListView.resetToActiveLegend();
  }
}

/** Why the action is blocked. Replaces the legend, so a greyed card is never unexplained. */
function reasonText(reason: QuoteActionBlockReason | undefined, ownerName: string): string {
  switch (reason) {
    case 'copying':
      return LangFranchiseeQuoteListView.reasonCopying();
    case 'deleting':
      return LangFranchiseeQuoteListView.reasonDeleting();
    case 'lockedByOther':
      return LangFranchiseeQuoteListView.reasonLockedByOther({ ownerName: ownerName || '' });
    case 'lockedBySelf':
      return LangFranchiseeQuoteListView.reasonLockedBySelf();
    case 'projectInactive':
      return LangFranchiseeQuoteListView.reasonProjectInactive();
    case 'setWillNotTakeAnother':
      return LangFranchiseeQuoteListView.reasonSetWillNotTakeAnother();
    default:
      return LangFranchiseeQuoteListView.reasonNotAllowedInThisState();
  }
}
